import React, { useEffect, useState } from 'react';

// HolidayBanner — renders a sitewide announcement banner during an active
// 48-hour holiday discount window. Polls /api/holiday-discount on mount.
// Dismissible (per-window) via localStorage so it doesn't pester customers
// who already saw it once. Re-appears for each new holiday.
//
// Shows:
//   HOLIDAY_NAME — 15% OFF SITEWIDE
//   (excludes instruments & items over $400)
//   Ends in HH:MM:SS · Offer expires DATE
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function HolidayBanner() {
  const [holiday, setHoliday] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(BACKEND + '/api/holiday-discount')
      .then((r) => r.json())
      .then((d) => { if (!cancelled) setHoliday(d.current || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Live countdown — update every second so the "Ends in HH:MM:SS" stays accurate.
  useEffect(() => {
    if (!holiday) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [holiday]);

  // Restore dismissal preference per-holiday so a user who closed the
  // Father's Day banner still sees the Black Friday one in November.
  useEffect(() => {
    if (!holiday) return;
    try {
      const key = 'jimendez_holiday_dismissed_' + holiday.slug + '_' + holiday.ends_at;
      if (window.localStorage.getItem(key) === '1') setDismissed(true);
    } catch (_e) { /* SSR safety */ }
  }, [holiday]);

  // Publish the banner's actual height as a CSS variable so the fixed nav
  // can shift below it (otherwise nav and banner stack on top of each
  // other and the banner covers the menu). On dismiss/unmount we clear the
  // var so the nav slides back to top-0.
  const bannerHeight = 36; // matches py-1.5 md:py-2 below
  useEffect(() => {
    const root = document.documentElement;
    if (holiday && !dismissed) {
      root.style.setProperty('--holiday-banner-h', bannerHeight + 'px');
    } else {
      root.style.setProperty('--holiday-banner-h', '0px');
    }
    return () => { root.style.setProperty('--holiday-banner-h', '0px'); };
  }, [holiday, dismissed]);

  if (!holiday || dismissed) return null;

  const endsMs = new Date(holiday.ends_at).getTime();
  const remainingMs = Math.max(0, endsMs - now);
  if (remainingMs <= 0) return null;

  const secs = Math.floor(remainingMs / 1000);
  const hh = String(Math.floor(secs / 3600)).padStart(2, '0');
  const mm = String(Math.floor((secs % 3600) / 60)).padStart(2, '0');
  const ss = String(secs % 60).padStart(2, '0');

  const expiryLabel = new Date(holiday.ends_at).toLocaleString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
  });

  function dismiss() {
    setDismissed(true);
    try {
      window.localStorage.setItem(
        'jimendez_holiday_dismissed_' + holiday.slug + '_' + holiday.ends_at,
        '1'
      );
    } catch (_e) {}
  }

  // Friendly upper-case headline: "FATHER'S DAY — 15% OFF SITEWIDE"
  const headline = (holiday.name || 'HOLIDAY').toUpperCase() +
    ' — ' + Math.round(holiday.percent_off) + '% OFF SITEWIDE';

  return (
    <div
      data-testid="holiday-banner"
      className="fixed top-0 left-0 right-0 z-[70] w-full bg-gradient-to-r from-[#7a5b00] via-[#d4af37] to-[#7a5b00] text-black shadow-md"
      style={{ height: bannerHeight + 'px' }}
    >
      <div className="max-w-7xl mx-auto px-3 h-full flex items-center gap-3 text-center md:text-left">
        <div className="flex-1 min-w-0 flex items-center justify-center md:justify-start gap-2 flex-wrap leading-none">
          <span className="font-extrabold tracking-wide text-[11px] md:text-sm">
            🎉 {headline}
          </span>
          <span className="hidden md:inline text-[11px] opacity-80">
            · Excl. instruments &amp; $400+
          </span>
          <span className="font-semibold text-[11px] md:text-sm" data-testid="holiday-countdown">
            · {hh}:{mm}:{ss} left
          </span>
          <span className="hidden lg:inline text-[11px] opacity-75">
            · ends {expiryLabel}
          </span>
        </div>
        <button
          onClick={dismiss}
          aria-label="Dismiss banner"
          data-testid="holiday-banner-dismiss"
          className="text-black/60 hover:text-black text-xl leading-none px-1 flex-shrink-0"
        >×</button>
      </div>
    </div>
  );
}
